import { Component, HostBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { DialogAnimation, ModalDialogService } from '@frontend/sports/modal/feature';
import { Story } from '@frontend/sports/types/components/story-content';
import { StoryContentPayload, StoryContentShape } from '@frontend/sports/types/components/widget/types';
import { WidgetComponent } from '@frontend/sports/widget/core/feature';
import { HtmlNode } from '@frontend/vanilla/core';
import { Subscription } from 'rxjs';

import { StoryContentTrackingService } from './story-content-tracking.service';
import { ContentStory } from './story-content.model';
import { StoryContentService } from './story-content.service';
import { StoryPreviewComponent } from './story-preview.component';
import { StoryComponent } from './story-view/story.component';

@Component({
    selector: 'ms-story-content',
    templateUrl: './story-content.component.html',
    imports: [ScrollAdapterComponent, StoryPreviewComponent],
    providers: [StoryContentTrackingService],
})
export class StoryContentComponent extends WidgetComponent<StoryContentPayload> {
    @HostBinding('class') className = 'story-content-container';
    @HostBinding('class.expanded') expanded: boolean;
    @HostBinding('class.square') square = false;
    @HostBinding('class.circle') circle = false;

    @HostBinding('class.single') get strech(): boolean {
        return this.stories?.length === 1;
    }

    dismissable: boolean;
    stories: ContentStory[];
    storyWithImage?: string;
    title?: string;
    viewTitle?: string;
    isEpcotEnabled = false;

    private contentSubscription?: Subscription;

    constructor(
        public sitecore: Sitecore,
        private html: HtmlNode,
        private modal: ModalDialogService,
        private storyTracking: StoryContentTrackingService,
        private storyService: StoryContentService,
        private epcotConfig: EpcotConfig,
    ) {
        super();
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    protected override onData(data: StoryContentPayload): void {
        this.setStories(data.stories);

        this.dismissable = data.dismissable;
        this.title = data.title;
        this.viewTitle = data.viewTitle;

        this.square = data.shape === StoryContentShape.Square;
        this.circle = data.shape === StoryContentShape.Circle;

        this.expanded = !this.storyService.isViewed(data);
        this.hasData = !(this.storyService.isDismissed(this.config.id) || this.stories.length === 0);
        this.setWidgetLoaded();
    }

    toggle(): void {
        this.expanded = !this.expanded;
    }

    dismiss(event: Event): void {
        event.stopPropagation();

        this.storyService.setDismissed(this.config.id);
        this.storyTracking.trackStoryDismissModule();

        this.hidden = true;

        this.contentSubscription?.unsubscribe();
    }

    openStory(story: ContentStory): void {
        const storyIndex = this.stories.indexOf(story);
        this.storyTracking.trackStoryClick(story.title, storyIndex);

        this.html.setCssClass('popup-on', true);
        this.modal.openPopup(StoryComponent, {
            data: {
                stories: this.stories,
                activeStory: storyIndex,
            },
            settings: {
                cssClass: 'modal-popup story-content-modal',
                fit: false,
                showPageHeader: false,
                showDialogHeader: false,
                isModalDialog: true,
                animation: DialogAnimation.SlideInFromBottom,
                closeAnimation: DialogAnimation.SlideOutFromTop,
                afterResulted: () => {
                    this.html.setCssClass('popup-on', false);
                },
            },
        });
    }

    private setStories(stories: Story[]): void {
        this.contentSubscription?.unsubscribe();

        this.mapStories(stories);

        const storyWithImage = stories.find((s) => s.tileImage);
        this.storyWithImage = storyWithImage?.id;

        this.contentSubscription = this.storyService.contentChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.mapStories(stories));
    }

    private mapStories(stories: Story[]): void {
        this.stories = this.storyService.map(stories);
        this.hasData = this.stories.length > 0;
    }
}
